/**
 * @file submenu.component.tsx
 * @description Компонент для отоюражения и работы с дополнительным меню левого меню. Открывается в том случае, если есть extraItems.
 * @author Genotroid
 * @created 2025-09-03
 * @updated 2025-09-03
 * @dependencies [react-router-dom, react-redux, axios, @api/actions, classnames, @pages/default, react-transition-group, @components/ui/indicators/loader, @store/slices/menu-slice, @consta/uikit/useGlobalKeys, @consta/icons/IconClose]
 * @related-files [@store/slices/menu-slice, @store/index, @pages/default]
 */

import { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getRequest } from '@api/actions';
import cn from 'classnames';
import { Default } from '@pages/default';
import { Transition } from 'react-transition-group';
import { Loader } from '@components/ui/indicators/loader';
import { getActiveMainMenuItem, setSubmenuItems, getSubmenuItems } from '@store/slices/menu-slice';
import { isEmpty } from '@consta/uikit/__internal__/src/utils/object';
import { useGlobalKeys } from '@consta/uikit/useGlobalKeys';
import { AppDispatch } from '@store/index';
import { IconClose } from '@consta/icons/IconClose';
import { TransitionStatus } from 'react-transition-group/Transition';
import CSS from 'csstype';
import { TMenuItem } from '@src/types';
import axios from 'axios';

export default function Submenu() {
    const location = useLocation();
    const [notFoundPage, setNotFoundPage] = useState<boolean>(false);
    const menuNodeRef = useRef<HTMLDivElement>(null);
    const duration: number = 300;
    const activeItem: TMenuItem | null = useSelector(getActiveMainMenuItem);
    const extraItems: TMenuItem[] = useSelector(getSubmenuItems);
    const dispatch = useDispatch<AppDispatch>();
    const linkTarget: '_blank' | '_self' = useMemo(() => {
        return location.pathname !== '/' ? '_blank' : '_self';
    }, [location]);

    useGlobalKeys({
        Escape: () => {
            if (extraItems?.length) {
                setTimeout(() => onPageClose(), 50);
            }
        }
    });

    const onPageClose = () => {
        dispatch(setSubmenuItems([]));
    };

    const getMenuItems = async () => {
        if (!activeItem?.$route) {
            return;
            // return setErrors('Для пункта меню не указан route.');
        }

        setNotFoundPage(false);
        const response = await getRequest('menu', { route: activeItem.$route });

        if (axios.isAxiosError(response)) {
            setNotFoundPage(true);
            return;
        }

        dispatch(setSubmenuItems(response.data));
    };

    useEffect(() => {
        if (activeItem?.$route) {
            getMenuItems();
        }
    }, [activeItem]);

    useEffect(() => {
        if (location.pathname === '/') {
            document.title = import.meta.env.VITE_APP_TAB_TITLE;
        }
    }, [location]);

    if (notFoundPage) {
        return <Default />;
    }

    const defaultStyle = {
        transition: `${duration}ms ease-in-out`,
        opacity: 0,
        left: 0,
        width: '28.5806vmin',
        color: 'white',
        height: '100%',
        backgroundColor: 'rgb(32 56 69)',
        top: 0,
        padding: '5.506vmin 1.8001vmin 1.8001vmin 2.753vmin',
        zIndex: 1
    };

    const transitionStyles: Partial<Record<TransitionStatus, CSS.Properties>> = {
        entering: { left: 0, width: '28.5806vmin', opacity: 0 },
        entered: { left: '28.5806vmin', width: '46.7871vmin', opacity: 1 },
        exiting: { left: '28.5806vmin', width: '46.7871vmin', opacity: 1 },
        exited: { left: 0, width: '28.5806vmin', opacity: 0 }
    };

    const whiteScroll: Record<string, string> = {
        '--color-scroll-bg': 'rgba(246, 251, 253, 0.06)',
        '--color-scroll-thumb': 'rgba(246, 251, 253, 0.24)',
        '--color-scroll-thumb-hover': 'rgba(246, 251, 253, 0.32)'
    };

    if (!activeItem || isEmpty(activeItem)) {
        return;
    }

    return (
        <div className={cn('extraMenu fixed left-0 top-0 h-full z-[1]')}>
            {extraItems?.length > 0 && (
                <div className="extraMenu__overflow w-[100vw] h-full bg-black opacity-25" onClick={onPageClose} />
            )}

            <Transition
                nodeRef={menuNodeRef}
                in={Boolean(extraItems.length)}
                out={extraItems.length === 0}
                timeout={duration}
            >
                {(state) => (
                    <div
                        style={{
                            ...defaultStyle,
                            ...transitionStyles[state]
                        }}
                        className="extraMenu__container flex flex-col absolute overflow-y-auto"
                    >
                        <IconClose
                            className="absolute top-[2.1171vmin] right-[2.1171vmin] cursor-pointer"
                            onClick={onPageClose}
                            color="white"
                            size="s"
                        />

                        {extraItems?.length ? (
                            <div className="h-full relative overflow-y-auto" style={whiteScroll}>
                                <div className="absolute extraMenu__group gap-[4.2342vmin] flex flex-col h-full w-full">
                                    {extraItems.map((group: TMenuItem, groupkey: number) => (
                                        <div key={groupkey} className="flex flex-col">
                                            <div className="extraMenu__group-title text-[1.3761vmin] font-semibold">
                                                {group.title}
                                            </div>

                                            {group.$subMenu?.length && (
                                                <div className="extraMenu__group-container flex flex-col gap-[1.2703vmin] pt-[1.9054vmin] pl-[2.1171vmin]">
                                                    {group.$subMenu.map((link: TMenuItem, linkKey: number) => (
                                                        <div
                                                            className="extraMenu__link h-[2.56vmin] flex items-center justify-between"
                                                            key={linkKey}
                                                        >
                                                            <Link
                                                                target={linkTarget}
                                                                key={linkKey}
                                                                to={`${activeItem.$route}/${link.$route}`}
                                                                className="link w-[26.4128vmin]"
                                                                onClick={() => dispatch(setSubmenuItems([]))}
                                                            >
                                                                {link.title}
                                                            </Link>

                                                            {link.$extra?.length && (
                                                                <div className="extraMenu__link-extra">
                                                                    {link.$extra.map(
                                                                        (button: TMenuItem, buttonKey: number) => (
                                                                            <Link
                                                                                target={linkTarget}
                                                                                to={`${activeItem.$route}/${button.$route}`}
                                                                                className="link"
                                                                                key={buttonKey}
                                                                                onClick={() =>
                                                                                    dispatch(setSubmenuItems([]))
                                                                                }
                                                                            >
                                                                                <div
                                                                                    className={cn(
                                                                                        'rounded-full w-auto h-[2.56vmin] block',
                                                                                        {
                                                                                            'bg-white':
                                                                                                !link.$isDisabled,
                                                                                            'bg-[#d3d3d37a]':
                                                                                                link.$isDisabled
                                                                                        }
                                                                                    )}
                                                                                >
                                                                                    <div className="h-full text-[#00395CCC] hover:text-[#5babdf] py-[0.3176vmin] px-[0.8468vmin]">
                                                                                        {button.title}
                                                                                    </div>
                                                                                </div>
                                                                            </Link>
                                                                        )
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <Loader />
                        )}
                    </div>
                )}
            </Transition>
        </div>
    );
}
